/**
 * Détecte le pays de l'utilisateur basé sur la langue du navigateur
 * @returns {string} - Code pays ('FR', 'LU', 'MA', 'BE')
 */
export function detectUserCountry() {
    const language = navigator.language || navigator.userLanguage || 'fr-FR';
    const langLower = language.toLowerCase();

    if (langLower.includes('lu') || langLower === 'lb') {
        return 'LU';
    }
    if (langLower.includes('ma') || langLower === 'ar-ma') {
        return 'MA';
    }
    if (langLower.includes('be') || langLower === 'nl-be' || langLower === 'fr-be' || langLower === 'de-be') {
        return 'BE';
    }
    return 'FR';
}

/**
 * Calcule les jours fériés français pour une année donnée
 * @param {number} year - L'année pour laquelle calculer les jours fériés
 * @returns {Array} - Liste des jours fériés avec leur date et nom
 */
export function getFrenchHolidays(year) {
    const holidays = [];

    // Jours fériés fixes
    holidays.push({ date: `${year}-01-01`, name: 'Jour de l\'an' });
    holidays.push({ date: `${year}-05-01`, name: 'Fête du travail' });
    holidays.push({ date: `${year}-05-08`, name: 'Victoire 1945' });
    holidays.push({ date: `${year}-07-14`, name: 'Fête nationale' });
    holidays.push({ date: `${year}-08-15`, name: 'Assomption' });
    holidays.push({ date: `${year}-11-01`, name: 'Toussaint' });
    holidays.push({ date: `${year}-11-11`, name: 'Armistice 1918' });
    holidays.push({ date: `${year}-12-25`, name: 'Noël' });

    // Calcul de Pâques (algorithme de Meeus/Jones/Butcher)
    const easter = calculateEaster(year);

    // Lundi de Pâques (Pâques + 1 jour)
    const easterMonday = new Date(easter);
    easterMonday.setDate(easter.getDate() + 1);
    holidays.push({ date: formatDate(easterMonday), name: 'Lundi de Pâques' });

    // Ascension (Pâques + 39 jours)
    const ascension = new Date(easter);
    ascension.setDate(easter.getDate() + 39);
    holidays.push({ date: formatDate(ascension), name: 'Ascension' });

    // Lundi de Pentecôte (Pâques + 50 jours)
    const pentecostMonday = new Date(easter);
    pentecostMonday.setDate(easter.getDate() + 50);
    holidays.push({ date: formatDate(pentecostMonday), name: 'Lundi de Pentecôte' });

    return holidays;
}

/**
 * Calcule les jours fériés luxembourgeois pour une année donnée
 * @param {number} year - L'année pour laquelle calculer les jours fériés
 * @returns {Array} - Liste des jours fériés avec leur date et nom
 */
export function getLuxembourgHolidays(year) {
    const holidays = [];

    // Jours fériés fixes
    holidays.push({ date: `${year}-01-01`, name: 'Nouvel An' });
    holidays.push({ date: `${year}-05-01`, name: 'Fête du travail' });
    holidays.push({ date: `${year}-05-09`, name: 'Journée de l\'Europe' });
    holidays.push({ date: `${year}-06-23`, name: 'Fête nationale' });
    holidays.push({ date: `${year}-08-15`, name: 'Assomption' });
    holidays.push({ date: `${year}-11-01`, name: 'Toussaint' });
    holidays.push({ date: `${year}-12-25`, name: 'Noël' });
    holidays.push({ date: `${year}-12-26`, name: 'Saint-Étienne' });

    // Calcul de Pâques
    const easter = calculateEaster(year);

    // Lundi de Pâques
    const easterMonday = new Date(easter);
    easterMonday.setDate(easter.getDate() + 1);
    holidays.push({ date: formatDate(easterMonday), name: 'Lundi de Pâques' });

    // Ascension
    const ascension = new Date(easter);
    ascension.setDate(easter.getDate() + 39);
    holidays.push({ date: formatDate(ascension), name: 'Ascension' });

    // Lundi de Pentecôte
    const pentecostMonday = new Date(easter);
    pentecostMonday.setDate(easter.getDate() + 50);
    holidays.push({ date: formatDate(pentecostMonday), name: 'Lundi de Pentecôte' });

    return holidays;
}

/**
 * Calcule les jours fériés belges pour une année donnée
 * @param {number} year - L'année pour laquelle calculer les jours fériés
 * @returns {Array} - Liste des jours fériés avec leur date et nom
 */
export function getBelgiumHolidays(year) {
    const holidays = [];

    // Jours fériés fixes (10 jours fériés légaux en Belgique)
    holidays.push({ date: `${year}-01-01`, name: 'Nouvel An' });
    holidays.push({ date: `${year}-05-01`, name: 'Fête du travail' });
    holidays.push({ date: `${year}-07-21`, name: 'Fête nationale belge' });
    holidays.push({ date: `${year}-08-15`, name: 'Assomption' });
    holidays.push({ date: `${year}-11-01`, name: 'Toussaint' });
    holidays.push({ date: `${year}-11-11`, name: 'Armistice 1918' });
    holidays.push({ date: `${year}-12-25`, name: 'Noël' });

    // Calcul de Pâques
    const easter = calculateEaster(year);

    // Dimanche de Pâques
    holidays.push({ date: formatDate(easter), name: 'Pâques' });

    // Lundi de Pâques (Pâques + 1 jour)
    const easterMonday = new Date(easter);
    easterMonday.setDate(easter.getDate() + 1);
    holidays.push({ date: formatDate(easterMonday), name: 'Lundi de Pâques' });

    // Ascension (Pâques + 39 jours)
    const ascension = new Date(easter);
    ascension.setDate(easter.getDate() + 39);
    holidays.push({ date: formatDate(ascension), name: 'Ascension' });

    // Dimanche de Pentecôte (Pâques + 49 jours)
    const pentecost = new Date(easter);
    pentecost.setDate(easter.getDate() + 49);
    holidays.push({ date: formatDate(pentecost), name: 'Pentecôte' });

    // Lundi de Pentecôte (Pâques + 50 jours)
    const pentecostMonday = new Date(easter);
    pentecostMonday.setDate(easter.getDate() + 50);
    holidays.push({ date: formatDate(pentecostMonday), name: 'Lundi de Pentecôte' });

    return holidays;
}

/**
 * Calcule les jours fériés marocains pour une année donnée
 * Note: Les fêtes islamiques sont approximatives car basées sur le calendrier lunaire
 * @param {number} year - L'année pour laquelle calculer les jours fériés
 * @returns {Array} - Liste des jours fériés avec leur date et nom
 */
export function getMoroccoHolidays(year) {
    const holidays = [];

    // Jours fériés fixes (civils)
    holidays.push({ date: `${year}-01-01`, name: 'Nouvel An' });
    holidays.push({ date: `${year}-01-11`, name: 'Manifeste de l\'Indépendance' });
    holidays.push({ date: `${year}-05-01`, name: 'Fête du travail' });
    holidays.push({ date: `${year}-07-30`, name: 'Fête du Trône' });
    holidays.push({ date: `${year}-08-14`, name: 'Journée Oued Ed-Dahab' });
    holidays.push({ date: `${year}-08-20`, name: 'Révolution du Roi et du Peuple' });
    holidays.push({ date: `${year}-08-21`, name: 'Fête de la Jeunesse' });
    holidays.push({ date: `${year}-11-06`, name: 'Marche Verte' });
    holidays.push({ date: `${year}-11-18`, name: 'Fête de l\'Indépendance' });

    // Fêtes islamiques (dates approximatives - varient selon le calendrier lunaire)
    const islamicHolidays = getApproximateIslamicHolidays(year);
    holidays.push(...islamicHolidays);

    return holidays;
}

/**
 * Calcule les dates approximatives des fêtes islamiques
 * Note: Ces dates sont des approximations et peuvent varier de 1-2 jours
 * @param {number} year - L'année grégorienne
 * @returns {Array} - Liste des fêtes islamiques
 */
function getApproximateIslamicHolidays(year) {
    const holidays = [];

    // Les dates islamiques se décalent d'environ 11 jours par an
    // Base: 2024 dates approximatives
    const baseYear = 2024;
    const yearDiff = year - baseYear;
    const dayShift = Math.round(yearDiff * -10.875); // ~10.875 jours de décalage par an

    // Dates de référence 2024
    const islamicDates2024 = [
        { month: 4, day: 10, name: 'Aïd al-Fitr' },
        { month: 4, day: 11, name: 'Aïd al-Fitr (2ème jour)' },
        { month: 6, day: 16, name: 'Aïd al-Adha' },
        { month: 6, day: 17, name: 'Aïd al-Adha (2ème jour)' },
        { month: 7, day: 7, name: 'Nouvel An Hégirien' },
        { month: 9, day: 15, name: 'Mawlid (Naissance du Prophète)' },
    ];

    islamicDates2024.forEach(({ month, day, name }) => {
        const baseDate = new Date(baseYear, month - 1, day);
        baseDate.setDate(baseDate.getDate() + dayShift);

        // Ajuster si la date sort de l'année cible
        if (baseDate.getFullYear() === year) {
            holidays.push({ date: formatDate(baseDate), name });
        }
    });

    return holidays;
}

/**
 * Retourne les jours fériés selon le pays détecté ou spécifié
 * @param {number} year - L'année pour laquelle calculer les jours fériés
 * @param {string} [country] - Code pays optionnel ('FR', 'LU', 'MA', 'BE'). Si non spécifié, détection automatique.
 * @returns {Array} - Liste des jours fériés avec leur date et nom
 */
export function getHolidays(year, country = null) {
    const detectedCountry = country || detectUserCountry();

    switch (detectedCountry) {
        case 'LU':
            return getLuxembourgHolidays(year);
        case 'MA':
            return getMoroccoHolidays(year);
        case 'BE':
            return getBelgiumHolidays(year);
        case 'FR':
        default:
            return getFrenchHolidays(year);
    }
}

/**
 * Calcule la date de Pâques pour une année donnée
 * Algorithme de Meeus/Jones/Butcher
 */
function calculateEaster(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;

    return new Date(year, month - 1, day);
}

/**
 * Formate une date en string YYYY-MM-DD
 */
function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Retourne les jours fériés formatés pour FullCalendar (affichage background)
 * @param {number} startYear - Année de début
 * @param {number} endYear - Année de fin
 * @param {string} [country] - Code pays optionnel
 * @returns {Array} - Événements formatés pour FullCalendar
 */
export function getHolidaysForFullCalendar(startYear, endYear, country = null) {
    const events = [];

    for (let year = startYear; year <= endYear; year++) {
        const holidays = getHolidays(year, country);
        holidays.forEach(holiday => {
            events.push({
                id: `holiday-${holiday.date}`,
                title: holiday.name,
                start: holiday.date,
                allDay: true,
                display: 'background',
                backgroundColor: '#fee2e2',
                borderColor: '#fecaca',
                classNames: ['holiday-event'],
                extendedProps: {
                    isHoliday: true
                }
            });
        });
    }

    return events;
}

/**
 * Retourne les jours fériés formatés pour FullCalendar (affichage comme événement normal)
 * @param {Date|string} startDate - Date de début de la plage
 * @param {Date|string} endDate - Date de fin de la plage
 * @param {string} [country] - Code pays optionnel
 * @returns {Array} - Événements formatés pour FullCalendar
 */
export function getHolidaysAsEvents(startDate, endDate, country = null) {
    const events = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    const startYear = start.getFullYear();
    const endYear = end.getFullYear();

    for (let year = startYear; year <= endYear; year++) {
        const holidays = getHolidays(year, country);
        holidays.forEach(holiday => {
            const holidayDate = new Date(holiday.date);
            // Filtrer pour ne garder que les jours fériés dans la plage de dates
            if (holidayDate >= start && holidayDate <= end) {
                events.push({
                    id: `holiday-${holiday.date}`,
                    title: holiday.name,
                    start: holiday.date,
                    allDay: true,
                    backgroundColor: '#dc3545',
                    borderColor: '#dc3545',
                    textColor: '#ffffff',
                    extendedProps: {
                        isHoliday: true
                    }
                });
            }
        });
    }

    return events;
}
